import * as React from "react";
import { Button, Dialog } from "@/components/ui";
import { CTA } from "@/services/bedrock";
import { CTAWizardProvider, useCTAWizard } from "./CTAWizardContext";
import { CategoryStep } from "./components/CategoryStep";
import { ConfigureStep } from "./components/ConfigureStep";
import { ReviewStep } from "./components/ReviewStep";
import { WizardStepIndicator } from "./components/WizardStepIndicator";

interface CTAWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCta: CTA | null;
  playerId?: number;
  onConfirm: (cta: CTA) => void;
}

const CTAWizardDialogContent: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCta: CTA | null;
}> = ({ open, onOpenChange, initialCta }) => {
  const { step, handleBackFromConfigure, handleNextFromConfigure, handleConfirm, setStep } = useCTAWizard();

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" showCloseButton>
      <Dialog.Header>
        <Dialog.Title>{initialCta ? "Edit Call to Action" : "Set Up Call to Action"}</Dialog.Title>
        <Dialog.Description>Add a button that sends visitors wherever you want them to go.</Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="max-h-[65vh] overflow-y-auto flex flex-col gap-4">
        <WizardStepIndicator step={step} />
        {step === 1 && <CategoryStep />}
        {step === 2 && <ConfigureStep />}
        {step === 3 && <ReviewStep />}
      </Dialog.Content>

      {step > 1 && (
        <Dialog.Footer>
          <Button type="button" variant="outline" onClick={() => (step === 3 ? setStep(2) : handleBackFromConfigure())}>
            Back
          </Button>
          {step === 2 && (
            <Button type="button" onClick={handleNextFromConfigure}>
              Next
            </Button>
          )}
          {step === 3 && (
            <Button type="button" onClick={handleConfirm}>
              Confirm
            </Button>
          )}
        </Dialog.Footer>
      )}
    </Dialog>
  );
};

export const CTAWizardDialog: React.FC<CTAWizardDialogProps> = ({
  open,
  onOpenChange,
  initialCta,
  playerId,
  onConfirm,
}) => (
  <CTAWizardProvider
    open={open}
    onOpenChange={onOpenChange}
    initialCta={initialCta}
    playerId={playerId}
    onConfirm={onConfirm}
  >
    <CTAWizardDialogContent open={open} onOpenChange={onOpenChange} initialCta={initialCta} />
  </CTAWizardProvider>
);

export type { CTAWizardDialogProps };
